/**
 * Categories & items — the whole shape of your spending on one screen.
 *
 * Bucket › category › the things you buy. Categories and items were two
 * settings screens, which made you hold the relationship in your head: the
 * Items screen printed a category name on every row, and the Categories screen
 * said nothing about what was actually filed under each one. They are the same
 * question asked from two ends, so they are one screen.
 *
 * Note what this is NOT: the old category tree back in disguise. A category
 * had subcategories, and both levels were categories — which is why one of
 * them always had to be made unusable (AGENTS.md §3.5a(ii)). Here the second
 * level is items, which are a different kind of thing: a category is where
 * money counts, an item is what you bought. Nothing has to be forbidden to
 * keep the two apart.
 *
 * The bucket mapping is still the point of the screen. Every category carries
 * one, every expense filed against it counts towards it, and nothing is
 * inferred or hidden — a dot you cannot interrogate looks like the app
 * deciding things about your money.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { BucketColors, Category, Household, Product } from '@et/contracts';
import { BUCKET_LABELS, BUCKETS, SPENDING_BUCKETS, type Bucket } from '@et/core';
import { api, messageOf, type Identity } from './api.js';
import { ItemDetail } from './Items.js';

/**
 * The headings, in order. INCOME last and deliberately included: it is where
 * income SOURCES live, and hiding it was what made a second screen necessary.
 */
const HEADINGS: Bucket[] = [...SPENDING_BUCKETS, 'INCOME'];

/**
 * What the header's controls are narrowing the list to. Same shape and same
 * reasoning as the journal's (AGENTS.md §3.5e): search is held separately from
 * the facets, the controls live in the page header because that band is
 * already sticky, and only what is ON takes space.
 */
export interface CategoryFilters {
  bucket: string;
  archived: boolean;
}

export const NO_CATEGORY_FILTERS: CategoryFilters = { bucket: '', archived: false };

interface Props {
  identity: Identity;
  categories: Category[];
  products: Product[];
  bucketColors: BucketColors;
  onHouseholdChanged: (next: Household) => void;
  /** Owner-only: the categories themselves, their buckets and their fate. */
  canEdit: boolean;
  /** Anyone but a viewer: the items underneath them. */
  canEditItems: boolean;
  /** The header's search box. Matching happens here, against loaded rows. */
  q: string;
  filters: CategoryFilters;
  onChanged: (next: Category[]) => void;
  onProductsChanged: () => void;
}

export function Categories({
  identity,
  categories,
  products,
  bucketColors,
  onHouseholdChanged,
  canEdit,
  canEditItems,
  q,
  filters,
  onChanged,
  onProductsChanged,
}: Props) {
  const [busyId, setBusyId] = useState<string | null>(null);
  /**
   * Which item is opened up. Categories are never collapsed: the whole point
   * of the merge is seeing what is filed under each one, and a screen you have
   * to unfold before it tells you anything is the two-screen problem again in
   * one file.
   */
  const [openItem, setOpenItem] = useState<string | null>(null);
  /**
   * The colour being dragged in a picker, before it is committed on blur.
   * `<input type="color">` fires `change` on every movement, and a PATCH per
   * pixel of a gradient is a write storm — so the swatch follows the draft and
   * only the final value is sent.
   */
  const [draftColor, setDraftColor] = useState<{ key: string; value: string } | null>(null);
  /**
   * Renaming an ITEM. Held separately from the category rename above even
   * though the ids could never collide, because the two commit to different
   * endpoints — sharing the state would make the commit guess which.
   */
  const [renamingItem, setRenamingItem] = useState<string | null>(null);
  const [itemDraft, setItemDraft] = useState('');
  /** Which row is being renamed, and what has been typed into it so far. */
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const showArchived = filters.archived;

  /** Items indexed by the category they file under. */
  const itemsByCategory = useMemo(() => {
    const map = new Map<string, Product[]>();
    for (const product of products) {
      if (!product.categoryId) continue;
      const list = map.get(product.categoryId);
      if (list) list.push(product);
      else map.set(product.categoryId, [product]);
    }
    for (const list of map.values()) list.sort((a, b) => a.name.localeCompare(b.name));
    return map;
  }, [products]);

  /**
   * Bucket › category › items.
   *
   * INCOME is last and is not one of the three: it is what they divide
   * (AGENTS.md §3.5a). It holds exactly one category, which exists only
   * because `entry_split.category_id` is NOT NULL and is never picked by hand
   * — so it is shown with its sources underneath and none of the controls a
   * real category gets.
   *
   * Search matches a category by its own name (showing everything under it) or
   * by one of its items (showing only the items that matched) — because "where
   * did I put eggs" is the question a search box on this screen gets asked.
   */
  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();

    const passesFacets = (c: Category) =>
      (showArchived || !c.archived) && (!filters.bucket || c.bucket === filters.bucket);

    return HEADINGS.map((bucket) => ({
      bucket,
      rows: categories
        .filter((c) => c.bucket === bucket && passesFacets(c))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((category) => {
          const items = itemsByCategory.get(category.id) ?? [];
          if (!needle) return { category, items };
          if (category.name.toLowerCase().includes(needle)) return { category, items };
          return {
            category,
            items: items.filter((i) => i.name.toLowerCase().includes(needle)),
          };
        })
        .filter(
          ({ category, items }) =>
            !needle || category.name.toLowerCase().includes(needle) || items.length > 0,
        ),
    })).filter((group) => group.rows.length > 0);
  }, [categories, itemsByCategory, showArchived, q, filters.bucket]);

  /**
   * Items whose category was deleted out from under them.
   *
   * `product.category_id` is `ON DELETE SET NULL`, so this is a state the
   * database can produce at any time — and with items shown UNDER their
   * category, an orphan has nowhere to appear. Without this block it would be
   * invisible and unfixable while still turning up in the Add screen's
   * suggestions. A gap to close, never a state to choose (AGENTS.md
   * §3.5b(ii)): opening one offers the picker, and the block disappears when
   * the last one is filed.
   *
   * Hidden while a bucket facet is on, because an unfiled item is in no bucket
   * — showing it under "Needs" would be a lie.
   */
  const unfiled = useMemo(() => {
    if (filters.bucket) return [];
    const needle = q.trim().toLowerCase();
    return products
      .filter((p) => !p.categoryId && (!needle || p.name.toLowerCase().includes(needle)))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [products, q, filters.bucket]);

  /**
   * Delete is for a mistake; Archive is for something that was real and is
   * over. The server decides which — it counts what points at the category,
   * because the DATABASE is what enforces it (`ON DELETE RESTRICT` on
   * `entry_split.category_id`). Re-deriving the rule here would eventually
   * disagree with the constraint and offer a button that fails.
   */
  function blockers(c: Category): string[] {
    const out: string[] = [];
    if (c.blockedBy.entries > 0)
      out.push(`${c.blockedBy.entries} ${c.blockedBy.entries === 1 ? 'entry' : 'entries'}`);
    if (c.blockedBy.items > 0)
      out.push(`${c.blockedBy.items} ${c.blockedBy.items === 1 ? 'item' : 'items'}`);
    return out;
  }

  /**
   * The name, and the way to change it.
   *
   * Editing in place rather than behind a pencil: the row already carries a
   * bucket select and a Delete, and a third control for something you do once
   * per category would crowd the two you use constantly. Tapping the name is
   * also the whole gesture on a phone, where a 16px icon is a miss waiting to
   * happen.
   */
  function catName(c: Category) {
    if (renamingId === c.id)
      return (
        <span className="cat-name">
          <input
            className="cat-rename"
            value={draft}
            autoFocus
            disabled={busyId === c.id}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void commitRename(c);
              }
              // Escape abandons the edit. Without it the only way out of a
              // half-typed name is to type the old one back.
              if (e.key === 'Escape') {
                e.preventDefault();
                setRenamingId(null);
              }
            }}
            onBlur={() => void commitRename(c)}
            aria-label={`Rename ${c.name}`}
          />
        </span>
      );

    const label = (
      <>
        {c.name}
        {c.usageCount > 0 && <em className="cat-uses">{c.usageCount}×</em>}
        {c.archived && <span className="tag">archived</span>}
      </>
    );

    if (!canEdit) return <span className="cat-name">{label}</span>;

    return (
      <button
        type="button"
        className="cat-name cat-name-edit"
        onClick={() => {
          setRenamingId(c.id);
          setDraft(c.name);
        }}
        title="Rename"
      >
        {label}
      </button>
    );
  }

  async function commitRename(c: Category) {
    const name = draft.trim();
    setRenamingId(null);
    // Unchanged or emptied is a cancel, not a write. An empty name would be
    // refused by the API anyway; not sending it keeps the error out of the way.
    if (!name || name === c.name) return;
    await change(c.id, { name });
  }

  /**
   * Delete, Archive or Restore — whichever the category has earned. Shared by
   * every row, because a category created by mistake was for a long time the
   * only thing in this app you could make and never get rid of.
   */
  function rowAction(c: Category) {
    if (c.archived)
      return (
        <button
          type="button"
          className="chip chip-ghost cat-archive"
          disabled={busyId === c.id}
          onClick={() => change(c.id, { archived: false })}
        >
          Restore
        </button>
      );

    const why = blockers(c);
    if (why.length === 0)
      return (
        <button
          type="button"
          className="chip chip-ghost cat-archive"
          disabled={busyId === c.id}
          onClick={() => void remove(c)}
        >
          Delete
        </button>
      );

    return (
      <button
        type="button"
        className="chip chip-ghost cat-archive"
        disabled={busyId === c.id}
        onClick={() => change(c.id, { archived: true })}
        title={`Has ${why.join(', ')} — archiving keeps your history readable`}
      >
        Archive
      </button>
    );
  }

  async function remove(c: Category) {
    setBusyId(c.id);
    setError(null);
    try {
      onChanged(await api.deleteCategory(identity, c.id));
    } catch (e: unknown) {
      setError(messageOf(e));
    } finally {
      setBusyId(null);
    }
  }

  async function change(id: string, body: Parameters<typeof api.updateCategory>[2]) {
    setBusyId(id);
    setError(null);
    try {
      onChanged(await api.updateCategory(identity, id, body));
    } catch (e: unknown) {
      setError(messageOf(e));
    } finally {
      setBusyId(null);
    }
  }

  /**
   * The palette as the browser has resolved it — the household's choice where
   * it made one, the stylesheet's light or dark value where it did not.
   *
   * Only ever used to seed a colour input, so it opens on the colour you can
   * actually see rather than on black. It is read from CSS rather than kept as
   * data because the defaults have two values per bucket and the stylesheet is
   * the only place both live.
   */
  const resolved = useResolvedBucketColors(bucketColors);

  async function setBucketColor(bucket: Bucket, value: string | null) {
    setBusyId(`bucket:${bucket}`);
    setError(null);
    try {
      onHouseholdChanged(
        await api.updateHousehold(identity, { bucketColors: { [bucket]: value } }),
      );
    } catch (e: unknown) {
      setError(messageOf(e));
    } finally {
      setBusyId(null);
    }
  }

  /**
   * The dot IS the control.
   *
   * Every category already carries one and you already look at it to read the
   * bucket; making it the thing you click adds a capability without adding a
   * control to a row that has three. The native input is kept in the DOM and
   * merely made transparent over the dot, so it stays focusable and keyboard
   * reachable rather than being replaced by a div that only mice can use.
   */
  function colorControl(
    key: string,
    current: string | null,
    fallback: string,
    className: string,
    label: string,
    onPick: (value: string | null) => void,
  ) {
    const draft = draftColor?.key === key ? draftColor.value : null;
    const shown = draft ?? current;

    const swatch = (
      <span
        className={shown ? 'dot' : `dot ${className}`}
        style={shown ? { background: shown } : undefined}
        aria-hidden="true"
      />
    );

    if (!canEdit) return swatch;

    return (
      <span className="cat-dot">
        <label>
          <input
            type="color"
            className="color-input"
            value={current ?? fallback}
            disabled={busyId === key}
            onChange={(e) => setDraftColor({ key, value: e.target.value.toLowerCase() })}
            onBlur={() => {
              const picked = draftColor?.key === key ? draftColor.value : null;
              setDraftColor(null);
              // Unchanged is not a write. Closing a picker you only looked at
              // should cost nothing.
              if (picked && picked !== current) onPick(picked);
            }}
            aria-label={label}
          />
          {swatch}
        </label>
        {current && (
          <button
            type="button"
            className="dot-reset"
            onClick={() => onPick(null)}
            disabled={busyId === key}
            title="Back to the default"
            aria-label={`Reset ${label}`}
          >
            ⟲
          </button>
        )}
      </span>
    );
  }

  /**
   * One item, and its panel when opened. Shared by the category blocks and by
   * the unfiled block, so an orphan is fixed with exactly the controls a filed
   * item has rather than a second, lesser form.
   */
  async function commitItemRename(p: Product) {
    const name = itemDraft.trim();
    setRenamingItem(null);
    if (!name || name === p.name) return;
    setBusyId(p.id);
    setError(null);
    try {
      await api.updateProduct(identity, p.id, { name });
      onProductsChanged();
    } catch (e: unknown) {
      setError(messageOf(e));
    } finally {
      setBusyId(null);
    }
  }

  /**
   * The item's name IS its rename control, exactly like a category's.
   *
   * It used to be part of one big button that expanded the row, with the
   * rename buried inside the panel that opened — so the gesture you learn on
   * the category two lines above did the wrong thing here. The row is a
   * container now, with the name as one button and the figures beside it as
   * another: click the name to rename, click the rest to see what you have
   * spent on it. Two buttons side by side, never nested.
   */
  function itemName(p: Product) {
    if (renamingItem === p.id)
      return (
        <input
          className="cat-rename cat-item-rename"
          value={itemDraft}
          autoFocus
          disabled={busyId === p.id}
          onChange={(e) => setItemDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void commitItemRename(p);
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              setRenamingItem(null);
            }
          }}
          onBlur={() => void commitItemRename(p)}
          aria-label={`Rename ${p.name}`}
        />
      );

    if (!canEditItems) return <span className="cat-item-name">{p.name}</span>;

    return (
      <button
        type="button"
        className="cat-item-name name-edit"
        onClick={() => {
          setRenamingItem(p.id);
          setItemDraft(p.name);
        }}
        title="Rename"
      >
        {p.name}
      </button>
    );
  }

  function itemRow(p: Product, kind: 'bought' | 'income') {
    return (
      <div className="cat-item-wrap" key={p.id}>
        <div className={openItem === p.id ? 'cat-item on' : 'cat-item'}>
          {itemName(p)}
          <button
            type="button"
            className="cat-item-open"
            onClick={() => setOpenItem(openItem === p.id ? null : p.id)}
            aria-expanded={openItem === p.id}
            aria-label={`What you have spent on ${p.name}`}
          >
            <span className="cat-item-figures">
              {p.timesBought > 0 && <em>{p.timesBought}×</em>}
              <span>{p.totalSpent}</span>
            </span>
          </button>
        </div>

        {openItem === p.id && (
          <ItemDetail
            identity={identity}
            id={p.id}
            kind={kind}
            categories={categories}
            canEdit={canEditItems}
            onChanged={onProductsChanged}
            onDeleted={() => {
              // Close first: leaving the panel open over a row that no longer
              // exists makes the list jump while the reload lands.
              setOpenItem(null);
              onProductsChanged();
            }}
          />
        )}
      </div>
    );
  }

  return (
    <section className="categories">
      <p className="hint">
        One list, one level. Each category counts towards one bucket — change it here and the Budget
        screen follows, including for expenses you have already recorded, since the bucket is read
        from the category rather than copied onto each entry. The things you buy sit underneath the
        category they file under; click one for what you have spent on it.
      </p>

      {!canEdit && <p className="hint">Only a household owner can change these.</p>}
      {error && <p className="error">{error}</p>}

      {canEdit && <AddCategory identity={identity} onChanged={onChanged} />}

      {groups.length === 0 && (
        <p className="hint">
          {q.trim()
            ? `Nothing matches “${q.trim()}” — no category, no item.`
            : categories.filter((c) => c.bucket !== 'INCOME').length === 0
              ? 'No categories yet. Add one above — name the places your money actually goes: “Groceries”, “Rent”, “Eating out” — then put the things you buy under them.'
              : 'Nothing matches these filters.'}
        </p>
      )}

      {/* Headed by bucket, which is the grouping the budget is built on. The
          heading is not a row: there is nothing to rename, archive or spend in
          — a bucket is a share of income, not a category (AGENTS.md §3.5a). */}
      {groups.map(({ bucket, rows }) => (
        <div className="cat-group" key={bucket}>
          <h3 className="cat-bucket">
            {colorControl(
              `bucket:${bucket}`,
              bucketColors[bucket],
              resolved[bucket],
              `dot-${bucket.toLowerCase()}`,
              `Colour for ${BUCKET_LABELS[bucket]}`,
              (value) => void setBucketColor(bucket, value),
            )}
            {BUCKET_LABELS[bucket]}
            <span className="cat-bucket-count">{rows.length}</span>
          </h3>

          {rows.map(({ category: c, items }) => (
            <div className="cat-block" key={c.id}>
              <div className="cat-row">
                {colorControl(
                  c.id,
                  c.color,
                  resolved[c.bucket],
                  `dot-${c.bucket.toLowerCase()}`,
                  `Colour for ${c.name}`,
                  (value) => void change(c.id, { color: value }),
                )}
                {catName(c)}

                {/* The Income category gets none of these. It is plumbing —
                    one row that exists because a split must name a category
                    (AGENTS.md §3.5d) — and a bucket select would offer to move
                    it somewhere it cannot come back from, while Delete would
                    make income unrecordable. */}
                {canEdit && c.bucket !== 'INCOME' && (
                  <>
                    <select
                      value={c.bucket}
                      disabled={busyId === c.id}
                      onChange={(e) => change(c.id, { bucket: e.target.value as Bucket })}
                      aria-label={`Bucket for ${c.name}`}
                    >
                      {BUCKETS.filter((b) => b !== 'INCOME').map((b) => (
                        <option key={b} value={b}>
                          {BUCKET_LABELS[b]}
                        </option>
                      ))}
                    </select>
                    {rowAction(c)}
                  </>
                )}
              </div>

              {items.map((p) => itemRow(p, c.bucket === 'INCOME' ? 'income' : 'bought'))}

              {canEditItems && !c.archived && (
                <AddItem
                  identity={identity}
                  category={c}
                  onAdded={onProductsChanged}
                />
              )}
            </div>
          ))}
        </div>
      ))}

      {unfiled.length > 0 && (
        <div className="cat-group">
          <h3 className="cat-bucket cat-bucket-unfiled">
            Not filed
            <span className="cat-bucket-count">{unfiled.length}</span>
          </h3>
          <div className="cat-block">
            <p className="hint cat-unfiled-note">
              The category these were filed under was deleted. Open one and choose where it
              belongs — until then they count towards no bucket.
            </p>
            {unfiled.map((p) => itemRow(p, 'bought'))}
          </div>
        </div>
      )}

    </section>
  );
}

// ---------------------------------------------------------------------------
// The filter half, rendered by App in the page header — same arrangement as
// the journal's, down to the CSS classes, so there is one filter shape in this
// app rather than two that merely resemble each other.
// ---------------------------------------------------------------------------

interface FilterProps {
  q: string;
  onQChange: (next: string) => void;
  filters: CategoryFilters;
  onFiltersChange: (next: CategoryFilters) => void;
  archivedCount: number;
}

export function CategoryFilterBar({
  q,
  onQChange,
  filters,
  onFiltersChange,
  archivedCount,
}: FilterProps) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLDivElement | null>(null);

  const facets = [filters.bucket, filters.archived ? 'archived' : ''].filter(Boolean).length;
  const narrowed = facets > 0 || q.trim().length > 0;

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!anchor.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function set(patch: Partial<CategoryFilters>) {
    onFiltersChange({ ...filters, ...patch });
  }

  return (
    <div className="filters">
      <input
        className="journal-search"
        type="search"
        value={q}
        onChange={(e) => onQChange(e.target.value)}
        placeholder="Search categories"
        aria-label="Search categories"
      />

      {narrowed && (
        <>
          {filters.bucket && (
            <FilterChip
              label={BUCKET_LABELS[filters.bucket as Bucket]}
              onClear={() => set({ bucket: '' })}
            />
          )}
          {filters.archived && (
            <FilterChip label="Archived shown" onClear={() => set({ archived: false })} />
          )}
          {q.trim() && <FilterChip label={`“${q.trim()}”`} onClear={() => onQChange('')} />}
          <button
            type="button"
            className="chip chip-ghost"
            onClick={() => {
              onQChange('');
              onFiltersChange(NO_CATEGORY_FILTERS);
            }}
          >
            Clear all
          </button>
        </>
      )}

      <div className="filter-anchor" ref={anchor}>
        <button
          type="button"
          className={open || facets > 0 ? 'chip chip-on' : 'chip'}
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          aria-haspopup="dialog"
        >
          <FilterIcon />
          Filter{facets > 0 ? ` · ${facets}` : ''}
        </button>

        {open && (
          <div className="filter-pop" role="dialog" aria-label="Filter categories">
            <label>
              <span>Bucket</span>
              <select value={filters.bucket} onChange={(e) => set({ bucket: e.target.value })}>
                <option value="">Every bucket</option>
                {BUCKETS.filter((b) => b !== 'INCOME').map((b) => (
                  <option key={b} value={b}>
                    {BUCKET_LABELS[b]}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Archived</span>
              <select
                value={filters.archived ? 'shown' : 'hidden'}
                onChange={(e) => set({ archived: e.target.value === 'shown' })}
              >
                <option value="hidden">Hidden</option>
                <option value="shown">
                  Shown{archivedCount > 0 ? ` (${archivedCount})` : ''}
                </option>
              </select>
            </label>
          </div>
        )}
      </div>
    </div>
  );
}

/** An applied filter, and the way to switch it off, in one control. */
function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <button type="button" className="chip chip-on filter-chip" onClick={onClear}>
      {label}
      <span aria-hidden="true">×</span>
      <span className="sr-only">Remove this filter</span>
    </button>
  );
}

/* Stroke icon on a 24px grid; inherits currentColor so both themes work. */
function FilterIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 6h16" />
      <path d="M7 12h10" />
      <path d="M10 18h4" />
    </svg>
  );
}

/**
 * A top-level category — the heading, not the place you spend.
 *
 * The bucket is asked for here and changeable on the row afterwards. It is the
 * only question the form asks beyond the name, because with one level there is
 * nothing else to decide.
 */
function AddCategory({
  identity,
  onChanged,
}: {
  identity: Identity;
  onChanged: (next: Category[]) => void;
}) {
  const [name, setName] = useState('');
  const [bucket, setBucket] = useState<Bucket>('NEEDS');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await api.createCategory(identity, { name: trimmed, bucket });
      setName('');
      onChanged(await api.categories(identity));
    } catch (e: unknown) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="cat-add-root">
      <div className="cat-row cat-add">
        <input
          placeholder="Add a category — “Groceries”, “Housing”…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void add();
            }
          }}
          aria-label="New category name"
        />
        <select
          value={bucket}
          onChange={(e) => setBucket(e.target.value as Bucket)}
          aria-label="Bucket for the new category"
        >
          {BUCKETS.filter((b) => b !== 'INCOME').map((b) => (
            <option key={b} value={b}>
              {BUCKET_LABELS[b]}
            </option>
          ))}
        </select>
        <button type="button" className="chip chip-on" disabled={busy || !name.trim()} onClick={() => void add()}>
          Add
        </button>
      </div>
      {error && <p className="error">{error}</p>}
    </div>
  );
}

/**
 * Put something in the catalogue without buying it first.
 *
 * The catalogue still grows by itself — every item exists because it was typed
 * on a line of a real purchase (AGENTS.md §3.5b) — and this is the other
 * direction: naming a thing before you have bought it, which a shopping list
 * wants and which this screen makes obvious now that items sit under the
 * category they belong to. No category picker, because the row you typed in IS
 * the answer.
 */
function AddItem({
  identity,
  category,
  onAdded,
}: {
  identity: Identity;
  category: Category;
  onAdded: () => void;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await api.createProduct(identity, { name: trimmed, categoryId: category.id });
      setName('');
      onAdded();
    } catch (e: unknown) {
      // Most often NAME_IS_A_CATEGORY or a duplicate: both are sentences worth
      // reading, and both belong beside the field that caused them.
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="cat-item-add">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void add();
          }
        }}
        placeholder={
          category.bucket === 'INCOME' ? 'Add a source — “Salary”…' : `Add an item to ${category.name}…`
        }
        aria-label={`Add an item to ${category.name}`}
        autoComplete="off"
      />
      <button type="button" className="chip" disabled={busy || !name.trim()} onClick={() => void add()}>
        Add
      </button>
      {error && <p className="error">{error}</p>}
    </div>
  );
}

/**
 * The palette as the browser has resolved it, keyed by bucket.
 *
 * Read from computed styles rather than kept as data because the defaults have
 * a light AND a dark value per bucket, and the stylesheet is the only place
 * both live. Re-read when the household's choices change and when the system
 * flips between light and dark, since the answer differs in each.
 *
 * Used ONLY to seed a colour input. Nothing on screen is painted from this —
 * the dots are CSS — so a value that is one frame stale costs nothing.
 */
function useResolvedBucketColors(chosen: BucketColors): Record<Bucket, string> {
  const [resolved, setResolved] = useState<Record<Bucket, string>>(readBucketColors);

  useEffect(() => {
    setResolved(readBucketColors());
  }, [chosen]);

  useEffect(() => {
    const dark = window.matchMedia('(prefers-color-scheme: dark)');
    const reread = () => setResolved(readBucketColors());
    dark.addEventListener('change', reread);
    return () => dark.removeEventListener('change', reread);
  }, []);

  return resolved;
}

function readBucketColors(): Record<Bucket, string> {
  const style = getComputedStyle(document.documentElement);
  const out = {} as Record<Bucket, string>;
  for (const bucket of BUCKETS) {
    const value = style.getPropertyValue(`--${bucket.toLowerCase()}`).trim().toLowerCase();
    // A colour input only accepts #rrggbb. Anything else — an unresolved
    // token, a named colour, an empty string before the stylesheet has loaded
    // — would make the input silently fall back to black, so a grey that
    // obviously means "unknown" is better.
    out[bucket] = /^#[0-9a-f]{6}$/.test(value) ? value : '#888888';
  }
  return out;
}
